/**
 * A COUNTERTOP MEASURED FROM THE CABINETS UNDER IT (spec §63C).
 *
 * Before the cabinet modules arrived, a countertop followed one cabinet RUN
 * and that was the whole relationship. A kitchen laid out as a 30" sink
 * base, an 18" base and a 36" drawer base has no run to follow — but it does
 * have a straight line of boxes against a wall, which is exactly what a
 * countertop covers.
 *
 * This works out that line. It refuses rather than guesses: cabinets facing
 * different ways, or with a gap between them, are not one countertop, and
 * saying so is more useful than averaging them into a slab that does not
 * exist.
 *
 * Pure geometry: plan units in, plan units out.
 */
import type { PlanModel, PlanObject, Point } from "./model";
import { objectPose } from "./objects";
import { PU_PER_INCH } from "./units";

/** Cabinets closer than this along the wall are one continuous surface. */
const GAP_TOLERANCE_PU = Math.round(2 * PU_PER_INCH);
/** Rotations within this are the same direction. */
const ANGLE_TOLERANCE_X10 = 50; // 5°
/** Centres further off the line than this are a different run. */
const OFFSET_TOLERANCE_PU = Math.round(3 * PU_PER_INCH);

export interface CountertopRun {
  /** The centre line of the top, end to end. */
  start: Point;
  end: Point;
  /** Cabinet depth — the caller adds the overhang. */
  cabinetDepthPu: number;
  rotationX10: number;
  /** The cabinets it was measured from, in order along the run. */
  cabinetIds: string[];
}

export type CountertopRunResult =
  | { ok: true; run: CountertopRun }
  | { ok: false; problem: string };

function sameDirection(a: number, b: number): boolean {
  const diff = Math.abs(((a - b) % 3600) + 3600) % 3600;
  return diff <= ANGLE_TOLERANCE_X10 || Math.abs(diff - 3600) <= ANGLE_TOLERANCE_X10;
}

/**
 * The countertop that covers these cabinets, or the reason there is not one.
 * `cabinets` must be the objects themselves — the caller decides which kinds
 * of cabinet carry a top (a wall cabinet does not).
 */
export function countertopRunFor(model: PlanModel, cabinets: readonly PlanObject[]): CountertopRunResult {
  const active = cabinets.filter((c) => c.archivedAt === null);
  if (active.length === 0) return { ok: false, problem: "There are no cabinets to measure from." };

  const first = active[0];
  const rotation = first.rotationX10;
  for (const cabinet of active) {
    if (!sameDirection(cabinet.rotationX10, rotation)) {
      return { ok: false, problem: "Those cabinets face different ways, so they are not one countertop." };
    }
  }

  // Project every centre onto the run's own direction.
  const rad = (rotation / 10) * (Math.PI / 180);
  const ux = Math.cos(rad);
  const uy = Math.sin(rad);
  const origin = objectPose(model, first).center;
  const projected = active
    .map((cabinet) => {
      const centre = objectPose(model, cabinet).center;
      const dx = centre.x - origin.x;
      const dy = centre.y - origin.y;
      return {
        cabinet,
        along: dx * ux + dy * uy,
        across: -dx * uy + dy * ux,
        halfWidth: cabinet.width / 2,
      };
    })
    .sort((a, b) => a.along - b.along);

  for (const item of projected) {
    if (Math.abs(item.across) > OFFSET_TOLERANCE_PU) {
      return { ok: false, problem: "Those cabinets are not in one line, so they are not one countertop." };
    }
  }

  for (let i = 1; i < projected.length; i += 1) {
    const previousEnd = projected[i - 1].along + projected[i - 1].halfWidth;
    const nextStart = projected[i].along - projected[i].halfWidth;
    if (nextStart - previousEnd > GAP_TOLERANCE_PU) {
      return { ok: false, problem: "There is a gap between those cabinets, so they are not one countertop." };
    }
  }

  const from = projected[0].along - projected[0].halfWidth;
  const to = projected[projected.length - 1].along + projected[projected.length - 1].halfWidth;
  const depth = Math.max(...projected.map((p) => p.cabinet.depth));

  return {
    ok: true,
    run: {
      start: { x: Math.round(origin.x + ux * from), y: Math.round(origin.y + uy * from) },
      end: { x: Math.round(origin.x + ux * to), y: Math.round(origin.y + uy * to) },
      cabinetDepthPu: depth,
      rotationX10: rotation,
      cabinetIds: projected.map((p) => p.cabinet.id),
    },
  };
}

/**
 * Has the run under an existing countertop changed? Compared in plan units
 * with a tolerance, so a rounding difference is not a change.
 */
export function runMatches(run: CountertopRun, countertop: PlanObject, overhangPu: number): boolean {
  if (countertop.endX === null || countertop.endY === null) return false;
  const tolerance = PU_PER_INCH;
  const sameEnds =
    Math.hypot(countertop.x - run.start.x, countertop.y - run.start.y) <= tolerance &&
    Math.hypot(countertop.endX - run.end.x, countertop.endY - run.end.y) <= tolerance;
  const sameDepth = Math.abs(countertop.depth - (run.cabinetDepthPu + overhangPu)) <= tolerance;
  return sameEnds && sameDepth;
}
